import React, { useState, useEffect } from 'react';
import {
  ShoppingBag, Search, Filter, Calendar, DollarSign, Clock,
  CheckCircle2, Truck, XCircle, AlertCircle, Eye, ChevronLeft,
  ChevronRight, RefreshCw, MapPin, User, Phone, Mail, CreditCard,
  X, Package, FileText, Check, Sparkles, Edit3
} from 'lucide-react';
import { authGet, authPut } from '../services/api';

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    all: 0,
    pending: 0,
    paid: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0
  });

  // Filters & Pagination
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [period, setPeriod] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);

  // Order Details Modal State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Status & Tracking Editing
  const [editStatus, setEditStatus] = useState('');
  const [editCourier, setEditCourier] = useState('');
  const [editTracking, setEditTracking] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [toast, setToast] = useState(null);

  // Fetch orders from API
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15',
        status: selectedStatus,
        period: period
      });
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const data = await authGet(`/api/admin/orders?${params.toString()}`);
      if (data?.success) {
        setOrders(data.orders || []);
        setTotalPages(data.totalPages || 1);
        setTotalOrdersCount(data.total || 0);
        if (data.metrics) {
          setMetrics(data.metrics);
        }
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
      showToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, period, page]);

  // Handle Search Submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  // Open Order Modal & Fetch Full Details
  const handleViewOrder = async (orderId) => {
    try {
      setIsModalOpen(true);
      setModalLoading(true);
      const res = await authGet(`/api/admin/orders/${orderId}`);
      if (res?.success && res.order) {
        const ord = res.order;
        setSelectedOrder(ord);
        setEditStatus(ord.status || 'pending');
        setEditCourier(ord.courier_name || '');
        setEditTracking(ord.tracking_number || '');
        setEditNotes(ord.admin_notes || '');
      } else {
        showToast('Order not found', 'error');
        setIsModalOpen(false);
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      showToast('Failed to load order details', 'error');
      setIsModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  // Save Status / Tracking / Notes Updates
  const handleSaveOrderUpdate = async () => {
    if (!selectedOrder) return;
    try {
      setSavingUpdate(true);
      const payload = {
        status: editStatus,
        courier_name: editCourier,
        tracking_number: editTracking,
        admin_notes: editNotes
      };

      const res = await authPut(`/api/admin/orders/${selectedOrder.id}/status`, payload);
      if (res?.success) {
        showToast(`Order #${selectedOrder.order_number || selectedOrder.id.slice(0, 8)} updated!`, 'success');
        setSelectedOrder(res.order || { ...selectedOrder, ...payload });
        fetchOrders(); // Refresh table list
      } else {
        showToast(res?.message || 'Update failed', 'error');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      showToast(err.message || 'Failed to update order', 'error');
    } finally {
      setSavingUpdate(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Status Badge Colors Helper
  const getStatusBadge = (status) => {
    const st = (status || '').toLowerCase();
    switch (st) {
      case 'pending':
        return { label: 'Pending', bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock };
      case 'paid':
        return { label: 'Paid', bg: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle2 };
      case 'processing':
        return { label: 'Processing', bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Package };
      case 'shipped':
        return { label: 'Shipped', bg: 'bg-purple-50 text-purple-700 border-purple-200', icon: Truck };
      case 'delivered':
        return { label: 'Delivered', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 };
      case 'cancelled':
        return { label: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle };
      default:
        return { label: status || 'Unknown', bg: 'bg-slate-100 text-slate-700 border-slate-200', icon: AlertCircle };
    }
  };

  // Format Currency
  const formatINR = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="space-y-8 pb-12 font-sans">
      
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-5 py-3.5 rounded-2xl shadow-2xl border flex items-center gap-3 transition-all duration-300 animate-in fade-in slide-in-from-top-4 ${
          toast.type === 'error' ? 'bg-rose-900 text-white border-rose-700' : 'bg-slate-900 text-white border-slate-700'
        }`}>
          {toast.type === 'error' ? <AlertCircle className="w-5 h-5 text-rose-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          <span className="text-sm font-bold">{toast.message}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-8 rounded-3xl border border-slate-200/80 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <span className="p-2 bg-orange-100 text-orange-600 rounded-xl">
              <ShoppingBag className="w-5 h-5" />
            </span>
            <span className="text-xs font-black text-orange-600 uppercase tracking-widest">Store Operations</span>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            Orders Management
          </h1>
          <p className="text-slate-500 text-sm font-medium mt-1">
            Track, update, and manage all customer orders in real-time
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchOrders()}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs tracking-wider transition-all active:scale-95"
            title="Refresh Orders"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics Summary Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Orders */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 hover:border-slate-300 transition-all hover:shadow-md">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Orders</span>
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <ShoppingBag className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{metrics.all}</div>
          <div className="text-[11px] text-slate-400 mt-1 font-medium">All registered orders</div>
        </div>

        {/* Pending Action */}
        <div className="bg-white p-5 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50/30 to-white hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Pending Action</span>
            <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-900">{metrics.pending + metrics.paid}</div>
          <div className="text-[11px] text-amber-600 mt-1 font-medium">Requires fulfillment</div>
        </div>

        {/* Processing */}
        <div className="bg-white p-5 rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50/30 to-white hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider">Processing</span>
            <div className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
              <Package className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-indigo-900">{metrics.processing}</div>
          <div className="text-[11px] text-indigo-600 mt-1 font-medium">In warehouse packing</div>
        </div>

        {/* Shipped & Delivered */}
        <div className="bg-white p-5 rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/30 to-white hover:shadow-md transition-all">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Completed</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-900">{metrics.shipped + metrics.delivered}</div>
          <div className="text-[11px] text-emerald-600 mt-1 font-medium">Shipped & Delivered</div>
        </div>

        {/* Total Revenue */}
        <div className="col-span-2 lg:col-span-1 bg-white p-5 rounded-2xl border border-slate-200/80 hover:border-slate-300 transition-all hover:shadow-md">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Revenue</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900">{formatINR(metrics.totalRevenue)}</div>
          <div className="text-[11px] text-slate-400 mt-1 font-medium">From successful orders</div>
        </div>
      </div>

      {/* Main Controls Card: Search & Filters */}
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm space-y-6">
        
        {/* Top Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          
          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-lg">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Order #, notes..."
              className="w-full pl-11 pr-24 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 text-sm font-medium placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:bg-white focus:ring-4 focus:ring-orange-500/10 transition-all"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-bold transition-all"
            >
              Search
            </button>
          </form>

          {/* Period Filter Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-slate-400" /> Period:
            </span>
            <select
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
              className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-xs font-bold focus:outline-none focus:border-orange-500 transition-all"
            >
              <option value="all">All Time</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
            </select>
          </div>
        </div>

        {/* Status Pill Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none border-b border-slate-100">
          {[
            { key: 'all', label: 'All Orders', count: metrics.all },
            { key: 'pending', label: 'Pending', count: metrics.pending },
            { key: 'paid', label: 'Paid', count: metrics.paid },
            { key: 'processing', label: 'Processing', count: metrics.processing },
            { key: 'shipped', label: 'Shipped', count: metrics.shipped },
            { key: 'delivered', label: 'Delivered', count: metrics.delivered },
            { key: 'cancelled', label: 'Cancelled', count: metrics.cancelled },
          ].map((tab) => {
            const active = selectedStatus === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { setSelectedStatus(tab.key); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black transition-all whitespace-nowrap ${
                  active
                    ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/10'
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200/60'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                  active ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Orders Data Table */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-slate-500 font-bold text-xs uppercase tracking-wider">Loading orders...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
            <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-black text-slate-900 mb-1">No Orders Found</h3>
            <p className="text-slate-500 text-xs font-medium max-w-sm mx-auto">
              There are no orders matching your current search or status filter.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3.5 px-4">Order Details</th>
                  <th className="py-3.5 px-4">Customer</th>
                  <th className="py-3.5 px-4">Items</th>
                  <th className="py-3.5 px-4">Total Amount</th>
                  <th className="py-3.5 px-4">Payment</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {orders.map((ord) => {
                  const badge = getStatusBadge(ord.status);
                  const BadgeIcon = badge.icon;
                  const customerName = ord.customer?.name || ord.shipping_address?.full_name || 'Guest User';
                  const itemCount = ord.items?.length || 0;

                  return (
                    <tr key={ord.id} className="hover:bg-slate-50/80 transition-colors group">
                      
                      {/* Order Number & Date */}
                      <td className="py-4 px-4">
                        <div className="font-black text-slate-900 group-hover:text-orange-600 transition-colors flex items-center gap-1.5">
                          <span>#{ord.order_number || ord.id.slice(0, 8)}</span>
                        </div>
                        <div className="text-xs text-slate-400 font-medium mt-0.5">
                          {new Date(ord.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      </td>

                      {/* Customer Info */}
                      <td className="py-4 px-4">
                        <div className="font-bold text-slate-900">{customerName}</div>
                        <div className="text-xs text-slate-500 font-medium truncate max-w-[180px]">
                          {ord.customer?.email || ord.shipping_address?.phone || 'N/A'}
                        </div>
                      </td>

                      {/* Items */}
                      <td className="py-4 px-4">
                        <div className="font-semibold text-slate-800">
                          {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
                        </div>
                        <div className="text-xs text-slate-400 font-medium truncate max-w-[180px]">
                          {ord.items?.[0]?.product_name || 'Order Item'}
                        </div>
                      </td>

                      {/* Total Amount */}
                      <td className="py-4 px-4">
                        <div className="font-black text-slate-900">
                          {formatINR(ord.total_amount)}
                        </div>
                      </td>

                      {/* Payment Method & Status */}
                      <td className="py-4 px-4">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold uppercase tracking-wider">
                          <CreditCard className="w-3.5 h-3.5 text-slate-500" />
                          {ord.payment_method || 'Online'}
                        </div>
                      </td>

                      {/* Status Badge */}
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${badge.bg}`}>
                          <BadgeIcon className="w-3.5 h-3.5" />
                          {badge.label}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={() => handleViewOrder(ord.id)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-orange-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm hover:shadow-md active:scale-95"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {!loading && orders.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100 text-xs font-medium text-slate-500">
            <div>
              Showing <span className="font-bold text-slate-900">{orders.length}</span> of <span className="font-bold text-slate-900">{totalOrdersCount}</span> orders
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                className="p-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-slate-700 font-bold transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 bg-slate-100 rounded-lg text-slate-900 font-black">
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="p-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-slate-700 font-bold transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* ORDER DETAILS & EDIT MODAL */}
      {/* ========================================================================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white border border-slate-200 w-full max-w-4xl rounded-3xl shadow-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="p-6 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-black tracking-tight">
                    Order #{selectedOrder?.order_number || selectedOrder?.id?.slice(0, 8)}
                  </h2>
                  {selectedOrder && (
                    <span className={`px-3 py-1 rounded-xl text-xs font-bold border ${getStatusBadge(selectedOrder.status).bg}`}>
                      {getStatusBadge(selectedOrder.status).label}
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-xs font-medium mt-1">
                  Placed on {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-IN') : 'N/A'}
                </p>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="w-9 h-9 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-xl flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            {modalLoading ? (
              <div className="py-24 text-center">
                <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-slate-500 font-bold text-xs uppercase tracking-wider">Loading Order Details...</p>
              </div>
            ) : selectedOrder ? (
              <div className="p-6 md:p-8 space-y-8 overflow-y-auto flex-1">
                
                {/* Admin Status & Tracking Update Control Panel */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <Edit3 className="w-4 h-4 text-orange-600" /> Admin Status & Fulfillment Controls
                    </span>
                    <button
                      onClick={handleSaveOrderUpdate}
                      disabled={savingUpdate}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-orange-600/20 flex items-center gap-2 active:scale-95"
                    >
                      {savingUpdate ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Save Updates
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    {/* Status Select */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Order Status
                      </label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-orange-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {/* Courier Name */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Courier Partner
                      </label>
                      <input
                        type="text"
                        value={editCourier}
                        onChange={(e) => setEditCourier(e.target.value)}
                        placeholder="e.g. Delhivery, Bluedart, EcomExpress"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500"
                      />
                    </div>

                    {/* Tracking Number */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                        Tracking Number
                      </label>
                      <input
                        type="text"
                        value={editTracking}
                        onChange={(e) => setEditTracking(e.target.value)}
                        placeholder="Tracking AWB #"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>

                  {/* Admin Notes */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                      Internal Admin Notes
                    </label>
                    <textarea
                      rows={2}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add internal notes for warehouse staff or customer history..."
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 resize-none"
                    />
                  </div>
                </div>

                {/* Grid Section: Items & Customer Details */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  
                  {/* Left Column: Order Items & Breakdown */}
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-500" /> Order Items ({selectedOrder.items?.length || 0})
                      </h3>
                      
                      <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, idx) => (
                            <div key={idx} className="p-4 flex items-center gap-4 bg-white hover:bg-slate-50/50 transition-colors">
                              <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {item.product?.image_url ? (
                                  <img src={item.product.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                                ) : (
                                  <Package className="w-6 h-6 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900 text-sm truncate">{item.product_name}</div>
                                {item.variant_name && (
                                  <div className="text-xs text-slate-500 font-medium">Variant: {item.variant_name}</div>
                                )}
                                <div className="text-xs text-slate-400 mt-1 font-medium">
                                  Qty: {item.quantity} × {formatINR(item.unit_price)}
                                </div>
                              </div>
                              <div className="text-right font-black text-slate-900 text-sm">
                                {formatINR(item.total_price || (item.quantity * item.unit_price))}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-xs text-slate-400 font-medium text-center">No item details available</div>
                        )}
                      </div>
                    </div>

                    {/* Financial Summary Box */}
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200/80 space-y-2.5 text-xs font-medium">
                      <div className="flex justify-between text-slate-600">
                        <span>Subtotal</span>
                        <span className="font-bold text-slate-900">{formatINR(selectedOrder.subtotal || selectedOrder.total_amount)}</span>
                      </div>
                      {selectedOrder.discount_amount > 0 && (
                        <div className="flex justify-between text-emerald-600">
                          <span>Discount</span>
                          <span className="font-bold">-{formatINR(selectedOrder.discount_amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-slate-600">
                        <span>Shipping Charge</span>
                        <span className="font-bold text-slate-900">{formatINR(selectedOrder.shipping_charge || 0)}</span>
                      </div>
                      <div className="pt-2 border-t border-slate-200 flex justify-between text-sm font-black text-slate-900">
                        <span>Grand Total</span>
                        <span className="text-orange-600">{formatINR(selectedOrder.total_amount)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Customer & Shipping Details */}
                  <div className="space-y-6">
                    
                    {/* Customer Info Card */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                        <User className="w-4 h-4 text-slate-500" /> Customer Information
                      </h4>
                      <div className="space-y-2 text-xs font-medium">
                        <div className="font-bold text-slate-900 text-sm">
                          {selectedOrder.customer?.name || selectedOrder.shipping_address?.full_name || 'Guest User'}
                        </div>
                        {selectedOrder.customer?.email && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <span className="truncate">{selectedOrder.customer.email}</span>
                          </div>
                        )}
                        {(selectedOrder.customer?.phone || selectedOrder.shipping_address?.phone) && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-3.5 h-3.5 text-slate-400" />
                            <span>{selectedOrder.customer?.phone || selectedOrder.shipping_address?.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address Card */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
                        <MapPin className="w-4 h-4 text-slate-500" /> Shipping Address
                      </h4>
                      {selectedOrder.shipping_address ? (
                        <div className="text-xs text-slate-600 space-y-1 font-medium leading-relaxed">
                          <div className="font-bold text-slate-900">{selectedOrder.shipping_address.full_name}</div>
                          <div>{selectedOrder.shipping_address.address_line1}</div>
                          {selectedOrder.shipping_address.address_line2 && <div>{selectedOrder.shipping_address.address_line2}</div>}
                          <div>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}</div>
                          <div className="pt-1 text-slate-500 font-bold">Phone: {selectedOrder.shipping_address.phone}</div>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-400 font-medium">No shipping address recorded</div>
                      )}
                    </div>

                  </div>
                </div>

              </div>
            ) : null}

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminOrdersPage;
